import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
const WIDTH = 900;
const HEIGHT = 320;
const PADDING = {
  top: 20,
  right: 20,
  bottom: 36,
  left: 56
};
/**
 * Hand-rolled SVG line/area chart for /stats' time-series section - no
 * charting library exists anywhere in this codebase, and the data shape
 * here (a plain ordered list of {label, value} points) is simple enough
 * that adding one just for this felt like more risk (a new build-time
 * dependency, a new API to learn for one page) than it's worth. Scales via
 * viewBox to whatever width its container gives it, fixed aspect ratio.
 *
 * Hover tracks the nearest point to the cursor's x position (not
 * per-point hit targets - with up to ~250 months of data those would be a
 * few pixels wide each, too fiddly to hover precisely) and shows a
 * vertical guide line, a highlighted dot, and a small tooltip with the
 * exact label/value.
 *
 * attrs: { points: [{label, value}], color, formatValue?(value) }
 */
export default class StatsLineChart extends Component {
  hoverIndex = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.hoverIndex = null;
  }
  view() {
    const points = this.attrs.points || [];
    if (points.length === 0) {
      return m("div", {
        className: "StatsLineChart-empty"
      }, app.translator.trans('champis-others.forum.stats_page.no_data'));
    }
    const maxValue = Math.max(...points.map(p => p.value), 1);
    const innerWidth = WIDTH - PADDING.left - PADDING.right;
    const innerHeight = HEIGHT - PADDING.top - PADDING.bottom;
    const x = i => PADDING.left + (points.length === 1 ? innerWidth / 2 : i / (points.length - 1) * innerWidth);
    const y = v => PADDING.top + innerHeight - v / maxValue * innerHeight;
    const baseline = PADDING.top + innerHeight;
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y(p.value).toFixed(2)}`).join(' ');
    const areaPath = `${linePath} L ${x(points.length - 1).toFixed(2)} ${baseline.toFixed(2)} L ${x(0).toFixed(2)} ${baseline.toFixed(2)} Z`;
    const ticks = this.yTicks(maxValue);
    // Never more than ~12 x-axis labels regardless of point count (up to
    // ~250 months of data) - showing every single one would just overlap
    // into an unreadable smear.
    const labelStep = Math.max(1, Math.ceil(points.length / 12));
    const hovered = this.hoverIndex !== null ? points[this.hoverIndex] : null;
    return m("div", {
      className: "StatsLineChart"
    }, m("svg", {
      viewBox: `0 0 ${WIDTH} ${HEIGHT}`,
      className: "StatsLineChart-svg",
      onmousemove: e => this.onHover(e, points, x),
      onmouseleave: () => {
        this.hoverIndex = null;
      }
    }, this.svgChildren(points, ticks, x, y, areaPath, linePath, labelStep, baseline, hovered)), hovered && m("div", {
      className: "StatsLineChart-tooltip"
    }, m("strong", null, hovered.label), " : ", this.formatValue(hovered.value)));
  }

  // Builds the <svg>'s children as one flat, explicitly-keyed array rather
  // than the more natural-looking mix of {ticks.map(...)}, two bare
  // <path>s, {points.map(...)} and {hovered && <g>...} - Mithril requires
  // every vnode in a fragment to either *all* have keys or *all* not have
  // one, and a `false` hole (from a map callback's own `cond && <X/>`, or
  // from a plain `{cond && <X/>}` sibling when cond is falsy) counts as
  // "no key" even where the element would have had one had it rendered.
  // Mixing those holes in next to the ticks'/labels' keyed elements is
  // exactly the "vnodes must either all have keys or none have keys" crash
  // - same root cause, same fix, as SpeciesCorrectionsPage.js's
  // renderTable() earlier this session: build one array by hand so nothing
  // but real, keyed vnodes ever ends up in it.
  svgChildren(points, ticks, x, y, areaPath, linePath, labelStep, baseline, hovered) {
    const children = [];
    ticks.forEach(t => {
      children.push(m("g", {
        key: 'tick-' + t
      }, m("line", {
        x1: PADDING.left,
        x2: WIDTH - PADDING.right,
        y1: y(t),
        y2: y(t),
        className: "StatsLineChart-gridLine"
      }), m("text", {
        x: PADDING.left - 8,
        y: y(t),
        className: "StatsLineChart-yLabel",
        "text-anchor": "end",
        "dominant-baseline": "middle"
      }, this.formatValue(t))));
    });
    children.push(m("path", {
      key: "area",
      d: areaPath,
      className: "StatsLineChart-area",
      style: {
        fill: this.attrs.color
      }
    }));
    children.push(m("path", {
      key: "line",
      d: linePath,
      className: "StatsLineChart-line",
      style: {
        stroke: this.attrs.color
      }
    }));
    points.forEach((p, i) => {
      if (i % labelStep !== 0) return;
      children.push(m("text", {
        key: 'label-' + i,
        x: x(i),
        y: HEIGHT - 10,
        className: "StatsLineChart-xLabel",
        "text-anchor": "middle"
      }, p.label));
    });
    if (hovered) {
      children.push(m("g", {
        key: "hover"
      }, m("line", {
        x1: x(this.hoverIndex),
        x2: x(this.hoverIndex),
        y1: PADDING.top,
        y2: baseline,
        className: "StatsLineChart-hoverLine"
      }), m("circle", {
        cx: x(this.hoverIndex),
        cy: y(hovered.value),
        r: "4",
        className: "StatsLineChart-hoverDot",
        style: {
          fill: this.attrs.color
        }
      })));
    }
    return children;
  }
  onHover(e, points, xScale) {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width * WIDTH;
    let closest = 0;
    let closestDist = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(xScale(i) - relX);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    });
    this.hoverIndex = closest;
    m.redraw();
  }
  formatValue(value) {
    return this.attrs.formatValue ? this.attrs.formatValue(value) : new Intl.NumberFormat('fr-FR').format(Math.round(value));
  }
  yTicks(maxValue) {
    const step = this.niceStep(maxValue / 4);
    const ticks = [];
    for (let v = 0; v <= maxValue; v += step) {
      ticks.push(v);
    }
    return ticks;
  }

  // Rounds a raw "value per gridline" to a human-friendly 1/2/5 * 10^n step
  // (e.g. 137 -> 100, 480 -> 500) rather than whatever ugly fraction
  // maxValue/4 happens to produce.
  niceStep(rough) {
    const magnitude = Math.pow(10, Math.floor(Math.log10(rough || 1)));
    const residual = rough / magnitude;
    let niceResidual;
    if (residual > 5) niceResidual = 10;else if (residual > 2) niceResidual = 5;else if (residual > 1) niceResidual = 2;else niceResidual = 1;
    return niceResidual * magnitude;
  }
}
flarum.reg.add('champis-others', 'forum/components/StatsLineChart', StatsLineChart);